import {
  Alert,
  AlertTitle,
  AspectRatio,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Spacer,
  Spinner,
  Tag,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import BackButton from "../../components/BackButton";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { Gig, Venue } from "../../types/MusicFestivalTypes";
import { useContext, useEffect, useState } from "react";
import { MapContext } from "../../context/map.context";
import { useNavigate, useParams } from "react-router-dom";
import {
  getMockMusicFestGigs,
  getMockMusicFestVenues,
  useMusicFestivalGigs,
  useMusicFestivalVenues,
} from "../../data/musicFestival.api";
import { MapPin } from "../../components/Map";
import { convertDateToDateString } from "../../util/dateFormatters";
import { IoChevronBack } from "react-icons/io5";
import LocationButton from "../../components/LocationButton";

const GigsAtVenue = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();

  const toast = useToast();

  const { venues, setVenues, flyTo, setGigs, gigs, setPins, pins } =
    useContext(MapContext);

  const { data, loading } = useMusicFestivalVenues();
  const { data: gigData, loading: gigLoading } = useMusicFestivalGigs();
  // const gigs = getMockMusicFestGigs // replace with api

  const [currentVenue, setCurrentVenue] = useState<Venue>();
  const [searched, setSearched] = useState<boolean>(false);
  const [venueGigs, setVenueGigs] = useState<Gig[]>([]);

  // fetches venues and sets current venue
  const fetchCurrentVenueAndEvents = (id: string) => {
    const listOfVenues: Venue[] =
      data?.getEventLocations != null ? data.getEventLocations : [];
    const matchedVenue = listOfVenues.find((venue) => venue.id === id);
    setVenues(listOfVenues);
    if (matchedVenue !== undefined) {
      setCurrentVenue(matchedVenue);
    }
  };

  // Moves map to lancaster town centre instead of university campus
  useEffect(() => {
    flyTo({
      pitch: 1,
      bearing: 1,
      zoom: 16,
      center: {
        lng: currentVenue?.longitude ? currentVenue.longitude : -2.803113,
        lat: currentVenue?.latitude ? currentVenue.latitude : 54.048958,
      },
    });
  }, [currentVenue]);

  // renders new map pins if none are present
  useEffect(() => {
    const mapPins = venues.map((venue) => ({
      lng: venue.longitude,
      lat: venue.latitude,
      color: `${venue.id === locationId ? "blue" : "#E9464F"}`,
      onClick: () => navigate(`/musicfestival/${venue.id}`),
    }));
    setPins(mapPins);
  }, [venues, locationId]);

  // fetches current venue when url changes
  useEffect(() => {
    if (locationId !== undefined && data) {
      fetchCurrentVenueAndEvents(locationId);
    }
    // filters down to only gigs at the current venue
    let listOfGigs: Gig[] = [];
    if (gigs.length === 0 && gigData && gigData.getPublishedEvents) {
      listOfGigs = gigData.getPublishedEvents;
    } else {
      listOfGigs = gigs;
    }
    const gigsAtLocation = listOfGigs.filter((gig) => {
      if (gig.locations?.[0]?.id === locationId) {
        return true;
      } else {
        return false;
      }
    });
    // orderGigs in time order and filtersout past gigs
    const orderedVenueGigs = gigsAtLocation
      .sort(
        (a, b) =>
          new Date(a.startDateUTC).getTime() -
          new Date(b.startDateUTC).getTime()
      ) // I think the data might already but ordered but I am adding this to be safe. If loading is taking too long remove this line
      .filter((gig) => {
        if (new Date(gig.startDateUTC).getTime() > Date.now()) {
          return true;
        } else {
          return false;
        }
      }); // Removes old events from list
    setVenueGigs(orderedVenueGigs);
    setGigs(gigs);
  }, [locationId, data, gigData]);

  // renders error toast if venue is not found
  useEffect(() => {
    if (!currentVenue && !loading && searched) {
      toast({
        status: "error",
        title: "Venue Not Found",
        description:
          "Please return to venues page. If problem persists the venue may no longer exist.",
        position: "top",
        isClosable: true,
        duration: 5000,
      });
    }
    if (!loading) {
      setSearched(true);
    }
  }, [data, loading, searched]);

  console.log(venues);

  return (
    <>
      <HybridBottomSheetDrawer ml={"4em"} maxDrawerHeight={window.innerHeight * 0.75}>
        <IconButton
          aria-label={"Return to music festival home page button"}
          icon={<IoChevronBack />}
          zIndex={999}
          onClick={() => {
            navigate("/musicfestival");
          }}
          position="absolute"
          top={2}
          right={2}
        />

        <Box p={8}>
          <Flex mb={4} align="center">
            <Heading pr="1rem" size="md">
              Venue
            </Heading>
            <LocationButton variant="outline"></LocationButton>
          </Flex>
          <HStack align={"start"} w={"100%"} h={"5.5em"} spacing={"1em"}>
            {!currentVenue && loading && (
              <HStack w={"100%"} justify={"center"}>
                <Text>Loading Venue</Text>
                <Spinner />
              </HStack>
            )}
            {/* Error message when no venue is found (incorrect venue id or venue no longer exists) */}
            {!currentVenue && !loading && searched && (
              <Alert
                flexDirection={"column"}
                gap={1}
                status="error"
                h={"100%"}
                borderRadius={"1em"}
              >
                <AlertTitle>Could Not Find This Venue</AlertTitle>
                <Button size={"sm"} onClick={() => navigate("/musicfestival")}>
                  Return to Venues Page
                </Button>
              </Alert>
            )}
            {/* Renders Current Venue */}
            {currentVenue?.imageUrl && (
              <Image
                style={{ aspectRatio: "1/1" }}
                maxH={"100%"}
                borderRadius={"xl"}
                src={currentVenue.imageUrl}
                alt="Image of music festival venue"
              />
            )}
            <VStack align={"start"} justify={"start"} h={"100%"} spacing={0}>
              <Heading size={"sm"} mb={"0em"}>
                {currentVenue?.title}
              </Heading>
              <Spacer />
              <Text>{currentVenue?.description}</Text>
              <Spacer />
            </VStack>
          </HStack>
          <Flex mt={6} mb={6} justify="space-between" align="center">
            <Heading size="md">Gigs at Venue</Heading>
          </Flex>
          <VStack w={"100%"}>
            {venueGigs.length < 1 && gigLoading && (
              <HStack w={"100%"} justify={"center"}>
                <Text>Loading Gigs</Text>
                <Spinner />
              </HStack>
            )}
            {/* Message for no gigs at this venue */}
            {!loading && venueGigs.length === 0 && (
              <Text>There are no upcoming gigs at this venue</Text>
            )}
            {/* Renders array of gigs at the selected venue */}
            {venueGigs.map((gig) => {
              const dateString = convertDateToDateString(gig.startDateUTC);
              return (
                <VStack
                  onClick={() => {
                    navigate(`/musicfestival/${locationId}/${gig.id}`);
                  }}
                  align={"start"}
                  justify={"start"}
                  h={"100%"}
                  w={"100%"}
                  spacing={0}
                >
                  <HStack w={"100%"}>
                    <Heading size={"sm"} color={"#717D96"}>
                      {gig.eventCategory}
                    </Heading>
                    <Spacer />
                    <Tag bg={"#D4D4D8"} color={"#89898F"}>
                      {gig.tags[0].name}
                    </Tag>
                  </HStack>
                  <Heading size={"sm"}>{gig.eventName}</Heading>
                  <HStack w={"100%"}>
                    <Text>{dateString}</Text>
                    <Spacer />
                    <Text>
                      {gig.startTime} - {gig.endTime}
                    </Text>
                  </HStack>
                  <Text>{gig.locations?.[0].title}</Text>
                </VStack>
              );
            })}
          </VStack>
        </Box>
      </HybridBottomSheetDrawer>
    </>
  );
};

export default GigsAtVenue;
